import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Shield, 
  Search, 
  Database, 
  Trash2, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Play,
  Settings,
  Clock,
  Activity
} from 'lucide-react';
import { gdprService, type GdprDeletionConfig, type GdprValidationResult, type GdprDeletionRequest } from '@/services/gdpr/GdprDeletionService';
import { supabase } from '@/integrations/supabase/client';
import { ActiveDeletionRequestsTable } from './ActiveDeletionRequestsTable';
import { useIsMobile } from '@/hooks/use-mobile';

export function UnifiedGdprPanel() {
  const [userId, setUserId] = useState('');
  const [validationResult, setValidationResult] = useState<GdprValidationResult | null>(null);
  const [deletionRequests, setDeletionRequests] = useState<GdprDeletionRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);
  const [cronStatus, setCronStatus] = useState<any>(null);
  const isMobile = useIsMobile();
  
  // Configuration state with persistent storage
  const [config, setConfig] = useState<GdprDeletionConfig>({
    enablePoints: true,
    enableNotifications: true,
    validateCompliance: true,
    autoProcessPhase2: false,
    cooldownPeriodDays: 7
  });

  const isValidUUID = (uuid: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  // Load persistent settings on component mount
  useEffect(() => {
    loadPersistedSettings();
    loadCronStatus();
  }, []);

  const loadPersistedSettings = async () => {
    try {
      const { data: autoProcessSetting } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'auto_process_phase2_enabled')
        .maybeSingle();

      const { data: cooldownSetting } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'deletion_cooling_off_days')
        .maybeSingle();

      setConfig(prev => ({
        ...prev,
        autoProcessPhase2: autoProcessSetting?.value === true || autoProcessSetting?.value === 'true',
        cooldownPeriodDays: cooldownSetting?.value ? parseInt(String(cooldownSetting.value)) : 7
      }));
    } catch (error) {
      console.error('Error loading persisted settings:', error);
    }
  };

  const loadCronStatus = async () => {
    try {
      const { data: lastExecution } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'last_cron_execution')
        .maybeSingle();

      setCronStatus(lastExecution?.value || null);
    } catch (error) {
      console.error('Error loading cron status:', error);
    }
  };

  const savePersistedSetting = async (key: string, value: any) => {
    try {
      await supabase
        .from('app_settings')
        .upsert({
          key,
          value: value,
          updated_at: new Date().toISOString()
        });
      
      toast.success('Setting saved successfully');
    } catch (error) {
      console.error('Error saving setting:', error);
      toast.error('Failed to save setting');
    }
  };

  const handleConfigChange = (field: keyof GdprDeletionConfig, value: any) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    
    // Save persistent settings immediately
    if (field === 'autoProcessPhase2') {
      savePersistedSetting('auto_process_phase2_enabled', value);
    } else if (field === 'cooldownPeriodDays') {
      savePersistedSetting('deletion_cooling_off_days', value);
    }
  };

  const handleValidateUser = async () => {
    if (!userId.trim() || !isValidUUID(userId.trim())) {
      toast.error('Please enter a valid UUID');
      return;
    }

    setLoading(true);
    try {
      const result = await gdprService.validateCompliance(userId.trim());
      setValidationResult(result);
      
      if (result.isCompliant) {
        toast.success('User is GDPR compliant');
      } else {
        toast.warning(`User has ${result.issues.length} compliance issues`);
      }
    } catch (error: any) {
      toast.error('Validation failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExecutePhase1 = async (targetUserId?: string) => {
    const userIdToProcess = targetUserId || userId.trim();
    
    if (!userIdToProcess || !isValidUUID(userIdToProcess)) {
      toast.error('Please enter a valid UUID');
      return;
    }

    setProcessing(`phase1-${userIdToProcess}`);
    try {
      const result = await gdprService.executePhase1(userIdToProcess, config);
      
      if (result.success) {
        toast.success('Phase 1 (anonymization) completed successfully');
        await loadDeletionRequests();
      } else {
        toast.error('Phase 1 failed: ' + result.error);
      }
    } catch (error: any) {
      toast.error('Phase 1 execution failed: ' + error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleExecutePhase2 = async (targetUserId: string) => {
    setProcessing(`phase2-${targetUserId}`);
    try {
      const result = await gdprService.executePhase2(targetUserId, config);
      
      if (result.success) {
        toast.success('Phase 2 (auth deletion) completed successfully');
        await loadDeletionRequests();
      } else {
        toast.error('Phase 2 failed: ' + result.error);
      }
    } catch (error: any) {
      toast.error('Phase 2 execution failed: ' + error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleProcessAllPending = async () => {
    setProcessing('all-pending');
    try {
      const results = await gdprService.processAllPendingRequests(config);
      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;
      
      toast.success(`Processed ${results.length} requests: ${successCount} successful, ${failureCount} failed`);
      await loadDeletionRequests();
      await loadCronStatus(); // Refresh cron status after processing
    } catch (error: any) {
      toast.error('Batch processing failed: ' + error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleTriggerCronJob = async () => {
    setProcessing('cron-trigger');
    try {
      const { data, error } = await supabase.functions.invoke('trigger-deletion-processing', {
        body: { manual_trigger: true }
      });

      if (error) throw error;

      toast.success('Cron job triggered successfully');
      await loadDeletionRequests();
      await loadCronStatus();
    } catch (error: any) {
      toast.error('Failed to trigger cron job: ' + error.message);
    } finally {
      setProcessing(null);
    }
  };

  const loadDeletionRequests = async () => {
    try {
      const requests = await gdprService.getDeletionRequests();
      setDeletionRequests(requests);
    } catch (error: any) {
      console.error('Error loading deletion requests:', error);
    }
  };

  useEffect(() => {
    loadDeletionRequests();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800';
      case 'failed': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800';
      case 'processing': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800';
      case 'pending_auth_deletion': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800';
      case 'cooling_off': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800';
      case 'auth_deletion_failed': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-300 border-gray-200 dark:border-gray-700';
    }
  };

  return (
    <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
          <Shield className="h-5 w-5" />
          Unified GDPR Deletion Management
        </CardTitle>
        <CardDescription className="text-gray-600 dark:text-gray-400">
          Centralized system for managing all phases of GDPR-compliant user deletion with debugging tools
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="validation" className="space-y-4">
          <div className={`${isMobile ? "overflow-x-auto pb-2" : ""}`}>
            <TabsList className={`${isMobile ? "w-full flex min-w-max" : "grid w-full grid-cols-5"} bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
              <TabsTrigger 
                value="validation" 
                className={`${isMobile ? "flex-1 py-2 px-3 text-xs whitespace-nowrap min-w-fit" : ""} data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100 data-[state=inactive]:text-gray-600 data-[state=inactive]:dark:text-gray-400`}
              >
                Validation
              </TabsTrigger>
              <TabsTrigger 
                value="operations" 
                className={`${isMobile ? "flex-1 py-2 px-3 text-xs whitespace-nowrap min-w-fit" : ""} data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100 data-[state=inactive]:text-gray-600 data-[state=inactive]:dark:text-gray-400`}
              >
                Operations
              </TabsTrigger>
              <TabsTrigger 
                value="requests" 
                className={`${isMobile ? "flex-1 py-2 px-3 text-xs whitespace-nowrap min-w-fit" : ""} data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100 data-[state=inactive]:text-gray-600 data-[state=inactive]:dark:text-gray-400`}
              >
                Requests
              </TabsTrigger>
              <TabsTrigger 
                value="monitoring" 
                className={`${isMobile ? "flex-1 py-2 px-3 text-xs whitespace-nowrap min-w-fit" : ""} data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100 data-[state=inactive]:text-gray-600 data-[state=inactive]:dark:text-gray-400`}
              >
                Monitoring
              </TabsTrigger>
              <TabsTrigger 
                value="config" 
                className={`${isMobile ? "flex-1 py-2 px-3 text-xs whitespace-nowrap min-w-fit" : ""} data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100 data-[state=inactive]:text-gray-600 data-[state=inactive]:dark:text-gray-400`}
              >
                Configuration
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="validation" className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="validationUserId">User ID to Validate</Label>
                <Input
                  id="validationUserId"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="Enter user UUID for validation"
                  className={userId.trim() && !isValidUUID(userId.trim()) ? 'border-red-300' : ''}
                />
              </div>

              <Button 
                onClick={handleValidateUser}
                disabled={loading || !userId.trim() || !isValidUUID(userId.trim())}
                className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    Validating...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-1" />
                    Validate GDPR Compliance
                  </>
                )}
              </Button>

              {validationResult && (
                <div className={`p-4 rounded-lg border ${
                  validationResult.isCompliant 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start gap-2">
                    {validationResult.isCompliant ? (
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${
                        validationResult.isCompliant 
                          ? 'text-green-800' 
                          : 'text-red-800'
                      }`}>
                        {validationResult.isCompliant 
                          ? '✅ User is ready for auth deletion' 
                          : `❌ User has ${validationResult.issues.length} compliance issues`
                        }
                      </p>
                      
                      <p className="text-sm text-gray-700 mt-1">
                        Validated across {validationResult.tablesChecked} tables
                      </p>
                      
                      {validationResult.issues.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-red-700">Issues found:</p>
                          <ul className="text-sm text-red-600 mt-1 list-disc list-inside max-h-40 overflow-y-auto">
                            {validationResult.issues.map((issue, index) => (
                              <li key={index} className="break-words">{issue}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {validationResult.detailedCounts && (
                        <div className="mt-3 p-2 bg-gray-50 rounded text-xs border">
                          <p className="font-medium text-gray-700 mb-1 flex items-center gap-1">
                            <Database className="h-3 w-3" />
                            Detailed Table Counts:
                          </p>
                          <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-3'} gap-1 text-gray-600`}>
                            {Object.entries(validationResult.detailedCounts).map(([table, count]) => (
                              <div key={table} className={`${count > 0 ? 'text-red-600 font-medium' : ''} break-words text-xs`}>
                                {table}: {count}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="operations" className="space-y-4">
            <div className="grid gap-4">
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Play className="h-4 w-4" />
                  Manual Operations
                </h3>
                
                <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-2`}>
                  <Button
                    onClick={() => handleExecutePhase1()}
                    disabled={processing !== null || !userId.trim() || !isValidUUID(userId.trim())}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex-1 sm:flex-none"
                  >
                    {processing?.startsWith('phase1') ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                        {isMobile ? 'Running...' : 'Running Phase 1...'}
                      </>
                    ) : (
                      <>
                        <Database className="h-4 w-4 mr-1" />
                        {isMobile ? 'Phase 1' : 'Execute Phase 1 (Anonymization)'}
                      </>
                    )}
                  </Button>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium flex items-center gap-2 mb-4">
                    <RefreshCw className="h-4 w-4" />
                    Batch Operations
                  </h3>
                  
                  <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-2`}>
                    <Button
                      onClick={handleProcessAllPending}
                      disabled={processing !== null}
                      className="bg-purple-600 hover:bg-purple-700 text-white flex-1 sm:flex-none"
                    >
                      {processing === 'all-pending' ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          {isMobile ? 'Processing...' : 'Processing All Pending...'}
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-1" />
                          {isMobile ? 'Process All' : 'Process All Pending Requests'}
                        </>
                      )}
                    </Button>

                    <Button
                      onClick={handleTriggerCronJob}
                      disabled={processing !== null}
                      variant="outline"
                      className="border-orange-200 text-orange-700 hover:bg-orange-50 flex-1 sm:flex-none"
                    >
                      {processing === 'cron-trigger' ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          {isMobile ? 'Triggering...' : 'Triggering...'}
                        </>
                      ) : (
                        <>
                          <Clock className="h-4 w-4 mr-1" />
                          {isMobile ? 'Trigger Cron' : 'Trigger Cron Job Manually'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="requests" className="space-y-4">
            <ActiveDeletionRequestsTable
              deletionRequests={deletionRequests}
              processing={processing}
              onRefresh={loadDeletionRequests}
              onExecutePhase1={handleExecutePhase1}
              onExecutePhase2={handleExecutePhase2}
              getStatusColor={getStatusColor}
            />
          </TabsContent>

          <TabsContent value="monitoring" className="space-y-4">
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <h3 className="font-medium flex items-center gap-2 text-gray-900 dark:text-gray-100">
                  <Activity className="h-4 w-4" />
                  System Monitoring & Debugging
                </h3>
                <Button
                  onClick={() => { loadCronStatus(); loadDeletionRequests(); }}
                  variant="outline"
                  size="sm"
                  className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 w-full sm:w-auto"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refresh Status
                </Button>
              </div>

              {cronStatus && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">Last Cron Execution</h4>
                  <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4 text-sm`}>
                    <div>
                      <p className="text-blue-700 dark:text-blue-300 break-words">
                        <strong>Timestamp:</strong> {new Date(cronStatus.timestamp).toLocaleString()}
                      </p>
                      <p className="text-blue-700 dark:text-blue-300">
                        <strong>Processed:</strong> {cronStatus.processed_requests || 0} requests
                      </p>
                    </div>
                    <div>
                      <p className="text-blue-700 dark:text-blue-300">
                        <strong>Successful:</strong> {cronStatus.successful || 0}
                      </p>
                      <p className="text-blue-700 dark:text-blue-300">
                        <strong>Failed:</strong> {cronStatus.failed || 0}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="text-blue-700 dark:text-blue-300">
                      <strong>Auto-Process Phase 2:</strong> {cronStatus.auto_process_phase2 ? '✅ Enabled' : '❌ Disabled'}
                    </p>
                  </div>
                </div>
              )}

              <div className={`grid grid-cols-1 ${isMobile ? 'gap-3' : 'md:grid-cols-3 gap-4'}`}>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <h4 className="font-medium text-yellow-800 dark:text-yellow-300">Cooling Off</h4>
                  <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">
                    {deletionRequests.filter(r => r.status === 'cooling_off').length}
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400">Awaiting expiration</p>
                </div>

                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                  <h4 className="font-medium text-orange-800 dark:text-orange-300">Pending Auth Deletion</h4>
                  <p className="text-2xl font-bold text-orange-900 dark:text-orange-200">
                    {deletionRequests.filter(r => r.status === 'pending_auth_deletion').length}
                  </p>
                  <p className="text-sm text-orange-700 dark:text-orange-400">Ready for Phase 2</p>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <h4 className="font-medium text-green-800 dark:text-green-300">Completed</h4>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-200">
                    {deletionRequests.filter(r => r.status === 'completed').length}
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-400">Fully processed</p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Debugging Information</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <p className="break-words">• Current time: {new Date().toISOString()}</p>
                  <p>• Total deletion requests: {deletionRequests.length}</p>
                  <p>• Auto-Process Phase 2: {config.autoProcessPhase2 ? 'Enabled' : 'Disabled'}</p>
                  <p>• Cooling off period: {config.cooldownPeriodDays} days</p>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="config" className="space-y-4">
            <div className="space-y-6">
              <h3 className="font-medium flex items-center gap-2 text-gray-900 dark:text-gray-100">
                <Settings className="h-4 w-4" />
                GDPR Operation Configuration
              </h3>

              <div className="grid gap-4">
                <div className={`flex ${isMobile ? 'flex-col gap-3' : 'items-center justify-between'} p-3 bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg`}>
                  <div className="flex-1">
                    <Label htmlFor="enableNotifications" className="text-gray-900 dark:text-gray-100">Enable Email Notifications</Label>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Send confirmation and status update emails</p>
                  </div>
                  <Switch
                    id="enableNotifications"
                    checked={config.enableNotifications}
                    onCheckedChange={(checked) => handleConfigChange('enableNotifications', checked)}
                    className={isMobile ? 'self-start' : ''}
                  />
                </div>

                <div className={`flex ${isMobile ? 'flex-col gap-3' : 'items-center justify-between'} p-3 bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg`}>
                  <div className="flex-1">
                    <Label htmlFor="validateCompliance" className="text-gray-900 dark:text-gray-100">Auto-Validate Compliance</Label>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Automatically validate GDPR compliance after operations</p>
                  </div>
                  <Switch
                    id="validateCompliance"
                    checked={config.validateCompliance}
                    onCheckedChange={(checked) => handleConfigChange('validateCompliance', checked)}
                    className={isMobile ? 'self-start' : ''}
                  />
                </div>

                <div className={`flex ${isMobile ? 'flex-col gap-3' : 'items-center justify-between'} p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg`}>
                  <div className="flex-1">
                    <Label htmlFor="autoProcessPhase2" className="font-medium text-orange-800 dark:text-orange-300">Auto-Process Phase 2</Label>
                    <p className="text-sm text-orange-700 dark:text-orange-400">
                      Automatically proceed to auth deletion after anonymization. 
                      <br />
                      <strong>⚠️ Warning:</strong> When enabled, the system will complete full account deletion automatically.
                    </p>
                  </div>
                  <Switch
                    id="autoProcessPhase2"
                    checked={config.autoProcessPhase2}
                    onCheckedChange={(checked) => handleConfigChange('autoProcessPhase2', checked)}
                    className={isMobile ? 'self-start' : ''}
                  />
                </div>

                <div className="space-y-2 p-3 bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <Label htmlFor="cooldownDays" className="text-gray-900 dark:text-gray-100">Cooling Off Period (Days)</Label>
                  <Input
                    id="cooldownDays"
                    type="number"
                    min="1"
                    max="30"
                    value={config.cooldownPeriodDays}
                    onChange={(e) => handleConfigChange('cooldownPeriodDays', parseInt(e.target.value) || 7)}
                    className="w-full sm:w-24 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                  />
                  <p className="text-sm text-gray-600 dark:text-gray-400">Number of days before deletion request becomes active</p>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-800 dark:text-blue-300">Enhanced GDPR System Features</p>
                    <ul className="text-blue-700 dark:text-blue-400 mt-1 space-y-1">
                      <li>• Persistent configuration settings stored in database</li>
                      <li>• Enhanced cron job debugging and monitoring</li>
                      <li>• Improved date/timezone handling for cooling-off periods</li>
                      <li>• Comprehensive audit logging with phase tracking</li>
                      <li>• Automatic retry mechanism for failed operations</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
